import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import {
  getTierRequestStatus,
  type CustomerTier,
} from "@/features/account/api/tier-request";

// Panel Mayorista Slice B — hook que expone el tier del customer al
// frontend. Hoy el backend NO incluye `tier` en el payload de /me ni en
// el bootstrap; el único endpoint que lo devuelve es /me/tier-request
// (Slice 2). Este wrapper sobre React Query usa el MISMO queryKey que
// WholesalePage para compartir cache — cero requests extra cuando ambas
// pantallas están montadas.
//
// Estados que el consumer puede usar:
//   - isAuthenticated  → hay customer logueado (de useAuth)
//   - isLoading        → query en vuelo (solo cuando isAuthenticated)
//   - tier             → "RETAIL" | "WHOLESALE" | null (null si guest o sin data)
//   - isWholesale      → shortcut para tier === "WHOLESALE"
//   - wholesaleEnabled → interruptor del dueño (de StorefrontSettings)
//
// Si el customer no está logueado, el query se DESHABILITA (enabled=false)
// y el hook devuelve flags neutros — sin pegarle al backend.

const QUERY_KEY = ["storefront", "tier-request-status"] as const;
const STALE_TIME_MS = 5 * 60 * 1000;

type TierStatus = Awaited<ReturnType<typeof getTierRequestStatus>>;

type AuthErrorLike = {
  response?: { status?: number };
  status?: number;
};

function isAuthError(error: unknown): boolean {
  const status =
    (error as AuthErrorLike)?.response?.status ??
    (error as AuthErrorLike)?.status;
  return status === 401 || status === 403;
}

export interface UseCustomerTierResult {
  tier: CustomerTier | null;
  isWholesale: boolean;
  wholesaleEnabled: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** raw data del tier-request endpoint (útil para WholesalePage). */
  data: TierStatus | undefined;
}

export function useCustomerTier(): UseCustomerTierResult {
  const { isAuthenticated } = useAuth();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getTierRequestStatus,
    enabled: isAuthenticated,
    staleTime: STALE_TIME_MS,
    retry: (count, err) => {
      if (isAuthError(err)) return false;
      return count < 2;
    },
  });

  return {
    tier: query.data?.currentTier ?? null,
    isWholesale: query.data?.currentTier === "WHOLESALE",
    wholesaleEnabled: Boolean(query.data?.wholesaleEnabled),
    isAuthenticated,
    isLoading: isAuthenticated && query.isLoading,
    data: query.data,
  };
}
